import {useEffect, useCallback} from 'react';
import {fabric} from 'fabric';

// eslint-disable-next-line no-underscore-dangle
const isImageObject = (obj) => obj && (obj.type === 'uYOImage' || obj._element);

export const useShadowImage = (canvas) => {
  const removeAllShadows = useCallback(() => {
    if (!canvas) return;

    const shadows = canvas.getObjects().filter((obj) => obj.isShadow);
    shadows.forEach((shadow) => canvas.remove(shadow));
    canvas.requestRenderAll();
  }, [canvas]);

  const createShadowImage = useCallback(
    (target) => {
      if (!canvas || !isImageObject(target)) return;

      removeAllShadows();

      // eslint-disable-next-line no-underscore-dangle
      const image = new fabric.Image(target._originalElement);
      image.set({
        ...target,
        isShadow: true,
        originalObjectId: target.id,
        opacity: 0.4,
        selectable: false,
        evented: false,
        clipPath: null,
        excludeFromExport: true,
      });

      canvas.add(image);
      canvas.renderAll();

      // eslint-disable-next-line no-param-reassign
      target.onDeselect = () => {
        canvas.remove(image);
        canvas.renderAll();
      };
    },
    [canvas, removeAllShadows]
  );

  const updateShadowPosition = useCallback(
    (target) => {
      if (!canvas || !isImageObject(target)) return;

      const shadow = canvas
        .getObjects()
        .find((obj) => obj.isShadow && obj.originalObjectId === target.id);

      if (shadow) {
        shadow.set({
          left: target.left,
          top: target.top,
          scaleX: target.scaleX,
          scaleY: target.scaleY,
          angle: target.angle,
          width: target.width,
          height: target.height,
          flipX: target.flipX,
          flipY: target.flipY,
          filters: target.filters,
        });

        shadow.applyFilters();
        canvas.renderAll();
      }
    },
    [canvas]
  );

  useEffect(() => {
    if (!canvas) return;

    const handleSelectionCreated = (e) => {
      const target = e.selected?.[0] || canvas.getActiveObject();
      if (isImageObject(target)) {
        createShadowImage(target);
      }
    };

    const handleSelectionUpdated = (e) => {
      const target = e.selected?.[0] || canvas.getActiveObject();
      if (isImageObject(target)) {
        createShadowImage(target);
      }
    };

    const handleObjectUpdate = ({target}) => {
      if (isImageObject(target)) {
        updateShadowPosition(target);
      }
    };

    const handleObjectModified = () => {
      const target = canvas.getActiveObject();
      if (isImageObject(target)) {
        updateShadowPosition(target);
      }
    };

    const events = {
      'selection:created': handleSelectionCreated,
      'selection:updated': handleSelectionUpdated,
      'object:moving': handleObjectUpdate,
      'object:scaling': handleObjectUpdate,
      'object:modified': handleObjectModified,
    };

    canvas.on(events);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off(events);
      removeAllShadows();
    };
  }, [canvas, createShadowImage, updateShadowPosition, removeAllShadows]);
};
